import { Mail, MousePointerClick, Video, UserCheck, TrendingUp } from "lucide-react";

/**
 * Visual mini-funnel showing a lead's journey:
 *   Kontaktiert → Klick → Webinar-Anmeldung → Onboarding → Live
 *
 * State is inferred from the lead/contact ``email_history`` array plus
 * (when present) the customer record's onboarding flags.
 *
 * Props:
 *   - emailHistory: array of {mail_id, status, clicked_at, clicks[]}
 *   - customer: optional customer_accounts object (for onboarding/live state)
 *   - createdAt: original lead created_at
 */
export default function LeadFunnel({ emailHistory = [], customer = null, createdAt = null, size = "md" }) {
  const compact = size === "sm";
  const hist = Array.isArray(emailHistory) ? emailHistory : [];

  // Step 1: Kontaktiert — created at all
  const sContacted = Boolean(createdAt) || hist.length > 0;

  // Step 2: Klick — clicked any drip mail
  const sClicked = hist.some(h => h?.clicked_at || (Array.isArray(h?.clicks) && h.clicks.length > 0));

  // Step 3: Webinar-Anmeldung — clicked a webinar invite or 3D/24H reminder
  const sRegistered = hist.some(h => {
    const id = h?.mail_id || "";
    const isWebinar = id === "WEBINAR_NEXT" || id.startsWith("WEBINAR:") ||
                       id.startsWith("WEBINAR_REMINDER_3D:") || id.startsWith("WEBINAR_REMINDER_24H:");
    if (!isWebinar) return false;
    return Boolean(h?.clicked_at) || (Array.isArray(h?.clicks) && h.clicks.length > 0);
  });

  // Step 4: Onboarding — exists in customer_accounts (regardless of webinar).
  // This covers: signed up directly, skipped the webinar, came through admin etc.
  const sOnboarding = Boolean(customer);
  // Sub-signal: onboarding "skip" active — webinar was bypassed via the
  // explicit Admin-Skip flow. We still want to surface this visually.
  const sSkipActive = Boolean(customer && customer.onboarding_skipped);

  // Step 5: Live — approved + MT5 set + alle Onboarding-Checks erledigt.
  // Vertrag-Waiving und Webinar-Skip befreien den Kunden nur von der
  // entsprechenden Vorstufe — sie ersetzen NICHT das eigentliche Abhaken
  // aller Videos/Schritte. Andernfalls würde z.B. ein vertrags-befreiter
  // Kunde fälschlich als "Live" angezeigt, ohne dass er das Onboarding
  // tatsächlich durchlaufen hat.
  const progress = customer?.onboarding_progress || {};
  const allStepsDone = Boolean(
    progress.all_completed === true ||
    (progress.video1_completed && progress.video2_completed)
  );
  const onboardingFertig = Boolean(
    customer &&
    (customer.status === "approved" || customer.status === "active") &&
    customer.mt5_account &&
    allStepsDone
  );
  const sLive = onboardingFertig;

  const steps = [
    { key: "contacted",  label: "Kontaktiert", icon: Mail, on: sContacted },
    { key: "clicked",    label: "Klick",        icon: MousePointerClick, on: sClicked },
    { key: "registered", label: "Webinar",      icon: Video, on: sRegistered },
    { key: "onboarding", label: "Onboarding",   icon: UserCheck, on: sOnboarding },
    { key: "live",       label: "Live",         icon: TrendingUp, on: sLive },
  ];

  const lastDoneIdx = (() => {
    let idx = -1;
    steps.forEach((s, i) => { if (s.on) idx = i; });
    return idx;
  })();

  const dotSize = compact ? "h-6 w-6" : "h-8 w-8";
  const lineH = compact ? "h-0.5" : "h-1";
  const lineMargin = compact ? "-mx-1.5" : "-mx-2";

  return (
    <div className="w-full" data-testid="lead-funnel">
      <div className="flex items-center w-full">
        {steps.map((s, i) => {
          const Icon = s.icon;
          const isDone = s.on;
          const isActive = !s.on && i === lastDoneIdx + 1;
          const nextDone = i < steps.length - 1 && steps[i + 1].on;
          return (
            <div key={s.key} className="flex items-center" style={{ flex: i === steps.length - 1 ? "0" : "1" }}>
              <div className="flex flex-col items-center">
                <div
                  className={`${dotSize} rounded-full flex items-center justify-center transition-all
                    ${isDone ? "bg-gradient-to-br from-[var(--al-purple)] to-fuchsia-500 text-white shadow-md shadow-violet-200"
                      : isActive ? "bg-amber-100 text-amber-700 ring-2 ring-amber-300 animate-pulse"
                      : "bg-slate-100 text-slate-400"}`}
                  title={s.label}
                  data-testid={`funnel-step-${s.key}-${isDone ? "done" : isActive ? "active" : "todo"}`}
                >
                  <Icon className={compact ? "h-3 w-3" : "h-4 w-4"} strokeWidth={2.5} />
                </div>
                {!compact && (
                  <span className={`mt-1 text-[10px] uppercase tracking-widest font-bold leading-none whitespace-nowrap
                    ${isDone ? "text-[var(--al-purple)]"
                      : isActive ? "text-amber-700"
                      : "text-slate-400"}`}>
                    {s.label}
                  </span>
                )}
              </div>
              {i < steps.length - 1 && (
                <div className={`flex-1 ${lineH} ${lineMargin} rounded-full ${nextDone || isDone ? "bg-gradient-to-r from-[var(--al-purple)] to-fuchsia-400" : "bg-slate-200"}`} />
              )}
            </div>
          );
        })}
      </div>
      {/* Status-Pills: zeigen den präzisen Onboarding-Sub-Status (auch wenn
          der Kunde das Webinar nie/noch nicht besucht hat). */}
      {(sLive || sSkipActive || sOnboarding) && (
        <div className="mt-2 flex items-center gap-1.5 flex-wrap" data-testid="funnel-status-pills">
          {sLive ? (
            <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest font-bold rounded-full px-2 py-0.5 bg-emerald-100 text-emerald-800 border border-emerald-200" data-testid="funnel-pill-onboarding-fertig">
              <UserCheck className="h-2.5 w-2.5" /> Onboarding fertig
            </span>
          ) : sSkipActive ? (
            <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest font-bold rounded-full px-2 py-0.5 bg-amber-100 text-amber-800 border border-amber-200" data-testid="funnel-pill-skip-aktiv">
              <Video className="h-2.5 w-2.5" /> Skip aktiv
            </span>
          ) : sOnboarding ? (
            <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest font-bold rounded-full px-2 py-0.5 bg-violet-100 text-violet-800 border border-violet-200" data-testid="funnel-pill-onboarding-aktiv">
              <UserCheck className="h-2.5 w-2.5" /> Onboarding läuft
            </span>
          ) : null}
        </div>
      )}
    </div>
  );
}
